import {createContext, use, useCallback, useEffect, useMemo, useRef, useState, type ReactNode} from 'react'

type CachePreheatContextType = {
  markTaskForPreheating: (key: string, task: () => Promise<unknown>, highPriority?: boolean) => void
  preheatTasks: () => Promise<void>
  resetQueue: () => void
}

type CachePreheatContextProviderProps = {
  children: ReactNode
  enabled?: boolean
}

type TasksToPreheat = Record<string, () => Promise<unknown>>

export const MAX_PREHEAT_TASKS = 50

const PREHEAT_REQUESTS_BATCH_SIZE = 4
const CachePreheatContext = createContext<CachePreheatContextType | null>(null)

export function CachePreheatContextProvider({children, enabled = true}: CachePreheatContextProviderProps) {
  const tasksToPreheatRef = useRef<TasksToPreheat>({})
  const keysRef = useRef<Set<string>>(new Set())
  const prioritizedKeysRef = useRef<Set<string>>(new Set())
  const preheatedRef = useRef<Set<string>>(new Set())
  const [pending, setPending] = useState(false)

  useEffect(() => {
    const tasks = tasksToPreheatRef.current
    const keys = keysRef.current
    const prioritizedKeys = prioritizedKeysRef.current
    const preheated = preheatedRef.current
    return () => {
      for (const key of Object.keys(tasks)) delete tasks[key]
      keys.clear()
      prioritizedKeys.clear()
      preheated.clear()
    }
  }, [])

  const markTaskForPreheating = useCallback(
    (key: string, task: () => Promise<unknown>, highPriority?: boolean) => {
      if (!enabled || preheatedRef.current.has(key)) return

      if (highPriority && keysRef.current.delete(key)) {
        prioritizedKeysRef.current.add(key)
        return
      }
      if (prioritizedKeysRef.current.has(key) || keysRef.current.has(key)) return

      tasksToPreheatRef.current[key] = task
      const targetKeys = highPriority ? prioritizedKeysRef.current : keysRef.current
      targetKeys.add(key)
      setPending(true)
    },
    [enabled],
  )

  const preheatTask = useCallback(
    async (key: string): Promise<boolean> => {
      if (!enabled || preheatedRef.current.has(key)) return false

      const task = tasksToPreheatRef.current[key]
      if (!task) return false

      preheatedRef.current.add(key)
      try {
        await task()
      } catch {
        // Preheating is best effort and must not break the current page.
      } finally {
        delete tasksToPreheatRef.current[key]
      }
      return true
    },
    [enabled],
  )

  const appendPreheatTasks = useCallback(
    (promises: Array<Promise<boolean>>, keys: Set<string>) => {
      for (const key of keys) {
        if (promises.length >= PREHEAT_REQUESTS_BATCH_SIZE) break
        if (!preheatedRef.current.has(key)) promises.push(preheatTask(key))
      }
      return promises
    },
    [preheatTask],
  )

  const preheatTasks = useCallback(async () => {
    const promises = appendPreheatTasks([], prioritizedKeysRef.current)
    appendPreheatTasks(promises, keysRef.current)
    await Promise.all(promises)

    const queuedKeys = new Set([...prioritizedKeysRef.current, ...keysRef.current])
    if (preheatedRef.current.size >= queuedKeys.size) setPending(false)
  }, [appendPreheatTasks])

  useEffect(() => {
    if (!enabled || !pending) return

    let cancelled = false
    const scheduleNextPreheat = () => {
      if (cancelled || !('requestIdleCallback' in window)) return

      requestIdleCallback(() => {
        if (cancelled || !pending) return
        const runPreheat = async () => {
          await preheatTasks()
          scheduleNextPreheat()
        }
        void runPreheat()
      })
    }

    scheduleNextPreheat()
    return () => {
      cancelled = true
    }
  }, [enabled, pending, preheatTasks])

  const resetQueue = useCallback(() => {
    for (const key of Object.keys(tasksToPreheatRef.current)) delete tasksToPreheatRef.current[key]
    keysRef.current.clear()
    prioritizedKeysRef.current.clear()
    preheatedRef.current.clear()
    setPending(false)
  }, [])

  const contextValue = useMemo(
    () => ({markTaskForPreheating, preheatTasks, resetQueue}),
    [markTaskForPreheating, preheatTasks, resetQueue],
  )

  return <CachePreheatContext value={contextValue}>{children}</CachePreheatContext>
}

export function useCachePreheatContext(): CachePreheatContextType {
  const context = use(CachePreheatContext)
  if (context) return context

  // eslint-disable-next-line no-console
  console.warn('useCachePreheatContext must be used within a CachePreheatContextProvider')
  return {
    markTaskForPreheating: () => {},
    preheatTasks: () => Promise.resolve(),
    resetQueue: () => {},
  }
}
